import { useForm, Link } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { formatCurrency } from '@/lib/utils'
import { useBasePath } from '@/lib/paths'
import { NomineeShareholderPayment } from '@/types'
import { CreditCard, CheckCircle2, Clock, Shield } from 'lucide-react'

type Props = {
  shareholder: { id: string; business_name: string }
  payment: NomineeShareholderPayment | null
  type: { gov_fee_cents: number; service_fee_cents: number; total_cents: number; name: string }
}

export default function NomineeShareholderPaymentShow({ shareholder, payment, type }: Props) {
  const basePath = useBasePath()
  const form = useForm({})
  const paymentCancelled = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('payment') === 'cancel'

  return (
    <DashboardLayout
      title={`Payment — ${shareholder.business_name}`}
      breadcrumbs={[
        { label: 'Nominee Shareholders', href: 'nominee_shareholders' },
        { label: shareholder.business_name, href: `nominee_shareholders/${shareholder.id}` },
        { label: 'Payment' },
      ]}
    >
      <div>
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-1">
            <CreditCard className="w-4 h-4 text-muted-foreground" />
            <h1 className="text-xl font-semibold tracking-tight">Payment</h1>
            {payment?.paid && (
              <Badge className="font-normal text-xs bg-green-600 text-white">Paid</Badge>
            )}
            {payment && !payment.paid && (
              <Badge className="font-normal text-xs bg-secondary text-secondary-foreground">Pending</Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{shareholder.business_name}</p>
        </div>

        {paymentCancelled && (
          <div className="border border-dashed rounded-lg p-4 mb-6 bg-muted/30">
            <p className="text-sm font-medium text-foreground">Payment cancelled</p>
            <p className="text-xs text-muted-foreground mt-1">You can try again when you're ready.</p>
          </div>
        )}

        {!payment ? (
          <div className="space-y-6">
            <section className="border rounded-lg">
              <div className="px-4 py-3 border-b">
                <h2 className="text-sm font-medium text-foreground">Summary</h2>
              </div>
              <div className="px-4 py-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Service Fee</span>
                  <span className="text-foreground">{formatCurrency(type.service_fee_cents)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Government Fee</span>
                  <span className="text-foreground">{formatCurrency(type.gov_fee_cents)}</span>
                </div>
                <div className="h-px bg-border my-1.5" />
                <div className="flex justify-between text-sm font-medium">
                  <span className="text-foreground">Total</span>
                  <span className="text-foreground">{formatCurrency(type.total_cents)}</span>
                </div>
              </div>
            </section>

            <div className="border rounded-lg px-4 py-3">
              <div className="flex items-start gap-3">
                <Shield className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">7-day money-back guarantee</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Full refund if we haven't started your service</p>
                </div>
              </div>
            </div>

            <form onSubmit={e => { e.preventDefault(); form.post(`${basePath}/nominee_shareholders/${shareholder.id}/payment`) }}>
              <Button type="submit" className="w-full" disabled={form.processing}>
                <CreditCard className="w-4 h-4 mr-2" />
                {form.processing ? 'Processing...' : `Pay ${formatCurrency(type.total_cents)}`}
              </Button>
            </form>
          </div>
        ) : payment.paid ? (
          <div className="space-y-6">
            <section className="border rounded-lg">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <h2 className="text-sm font-medium text-foreground">Payment Complete</h2>
                <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                  <CheckCircle2 className="w-3 h-3" />
                  Paid on {new Date(payment.paid_at!).toLocaleDateString()}
                </span>
              </div>
              <div className="px-4 py-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Service Fee</span>
                  <span className="text-foreground">{formatCurrency(payment.service_fee_cents)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Government Fee</span>
                  <span className="text-foreground">{formatCurrency(payment.gov_fee_cents)}</span>
                </div>
                <div className="h-px bg-border my-1.5" />
                <div className="flex justify-between text-sm font-medium">
                  <span className="text-foreground">Total Paid</span>
                  <span className="text-foreground">{formatCurrency(payment.total_cents)}</span>
                </div>
                {payment.receipt && (
                  <div className="flex justify-between text-xs text-muted-foreground pt-1">
                    <span>Receipt</span>
                    <span className="font-mono">{payment.receipt.number}</span>
                  </div>
                )}
                {payment.invoice && (
                  <div className="flex justify-between text-xs text-muted-foreground pt-0.5">
                    <span>Invoice</span>
                    <span className="font-mono">{payment.invoice.number}</span>
                  </div>
                )}
              </div>
            </section>

            <Button asChild variant="outline" className="w-full">
              <Link href={`${basePath}/nominee_shareholders/${shareholder.id}`}>Back to Service</Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <section className="border rounded-lg">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <h2 className="text-sm font-medium text-foreground">Payment Pending</h2>
                <span className="flex items-center gap-1 text-xs text-muted-foreground font-medium">
                  <Clock className="w-3 h-3" />
                  Complete checkout to proceed
                </span>
              </div>
              <div className="px-4 py-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Service Fee</span>
                  <span className="text-foreground">{formatCurrency(payment.service_fee_cents)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Government Fee</span>
                  <span className="text-foreground">{formatCurrency(payment.gov_fee_cents)}</span>
                </div>
                <div className="h-px bg-border my-1.5" />
                <div className="flex justify-between text-sm font-medium">
                  <span className="text-foreground">Total</span>
                  <span className="text-foreground">{formatCurrency(payment.total_cents)}</span>
                </div>
              </div>
            </section>

            <form onSubmit={e => { e.preventDefault(); form.post(`${basePath}/nominee_shareholders/${shareholder.id}/payment`) }}>
              <Button type="submit" className="w-full" disabled={form.processing}>
                <CreditCard className="w-4 h-4 mr-2" />
                {form.processing ? 'Redirecting...' : `Pay ${formatCurrency(payment.total_cents)}`}
              </Button>
            </form>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
